import React, { useCallback, useEffect, useRef } from 'react';
import { observer } from 'mobx-react';
import classnames from 'classnames';

import { UiButton, UiIcon } from 'shared/uikit-new';
import { useObservable, useOnClickOutside } from 'shared/hooks';
import { COLORS } from 'shared/constants';
import { CalendarItemModel, ICalendarItemModel } from 'shared/models';
import { CalendarItemEducationTypeEnum, CalendarItemTypeEnum } from 'shared/enums';
import { DateType } from 'shared/types';

import { Store } from 'pages/calendar/store';

import './styles.scss';

type PropsType = {
    className?: string;
    style?: React.CSSProperties;
    isOpened?: boolean;
    date: DateType;
    render?: (options: { onClick: () => void; isOpened: boolean }) => React.ReactNode;
    calendarItem?: ICalendarItemModel;
};

export const CalendarPageCreatingPopout = observer(function CalendarPageCreatingPopout({
    className, render, date, isOpened = false, calendarItem = {}, style = {}
}: PropsType) {
    const ref = useRef<HTMLDivElement>(null);
    const store = useObservable({
        isOpened,
        orientationHorizontal: 'right',
        orientationVertical: 'top',
    });

    useEffect(() => {
        store.set('isOpened', isOpened);
    }, [isOpened, store]);

    useOnClickOutside(ref, () => {
        store.set('isOpened', false);
    });

    const classNames = classnames('calendar-page-creating-popout', className, `calendar-page-creating-popout--${store.orientationHorizontal}`, `calendar-page-creating-popout--${store.orientationVertical}`, {
        'calendar-page-creating-popout--opened': store.isOpened,
        [`${className}--opened`]: store.isOpened,
        'calendar-page-creating-popout--default': !className,
    });

    const handleClick = useCallback(() => {
        if (!ref?.current) {
            return;
        }

        const bounds = ref.current.getBoundingClientRect();
        if (!store.isOpened) {
            store.set('orientationHorizontal', window.innerWidth - bounds.left - bounds.width > Math.min(300, window.innerWidth / 2) ? 'right' : 'left');
            store.set('orientationVertical', window.innerHeight - bounds.top - bounds.height > 200 ? 'top' : 'bottom');
        }

        store.set('isOpened', !store.isOpened);
    }, [ref, store]);

    const handleCreating = (typeId: string) => () => {
        store.update({
            isOpened: false,
        });
        Store.edit.update({
            isOpened: true,
            calendarItem: new CalendarItemModel({
                typeId,
                startAt: date.format(),
                endAt: date.add(30, 'minutes').format(),
                educationTypeId: typeId === CalendarItemTypeEnum.Education ? CalendarItemEducationTypeEnum.Online : null,
                ...calendarItem,
            }),
        });
    };

    return (
        <div
            className={classNames}
            style={style}
            ref={ref}
            onClick={() => {
                if (render) {
                    return;
                }
                handleClick();
            }}
        >
            {!!render &&
                render({
                    isOpened: store.isOpened,
                    onClick: handleClick,
                })}
            {store.isOpened && (
                <div className='calendar-page-creating-popout__outer'>
                    <UiButton
                        className='calendar-page-creating-popout__button'
                        style={{ backgroundColor: [COLORS._1CBA4C, COLORS._17963D], color: [COLORS.WHITE, COLORS.WHITE] }}
                        onClick={handleCreating(CalendarItemTypeEnum.Meeting)}
                    >
                        <UiIcon.CirclePlus size={20} />
                        <span>Событие</span>
                    </UiButton>
                    <UiButton
                        className='calendar-page-creating-popout__button'
                        style={{ backgroundColor: [COLORS._00A8FF, COLORS._0090DB], color: [COLORS.WHITE, COLORS.WHITE] }}
                        onClick={handleCreating(CalendarItemTypeEnum.Reminder)}
                    >
                        <UiIcon.CirclePlus size={20} />
                        <span>Напоминание</span>
                    </UiButton>
                    <UiButton
                        className='calendar-page-creating-popout__button'
                        style={{ backgroundColor: [COLORS._A55EEA, COLORS._8C50C7], color: [COLORS.WHITE, COLORS.WHITE] }}
                        onClick={() => {
                            store.set('isOpened', false);
                            Store.set('taskTrackerTask', {
                                id: 0,
                                expiredAt: date.format(),
                            });
                        }}
                    >
                        <UiIcon.CirclePlus size={20} />
                        <span>Задача</span>
                    </UiButton>
                    <UiButton
                        className='calendar-page-creating-popout__button'
                        style={{ backgroundColor: [COLORS._FFAA34, COLORS._DB922D], color: [COLORS.WHITE, COLORS.WHITE] }}
                        onClick={handleCreating(CalendarItemTypeEnum.Education)}
                    >
                        <UiIcon.CirclePlus size={20} />
                        <span>Обучение</span>
                    </UiButton>
                </div>
            )}
        </div>
    );
});
